import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder } from '@angular/forms';
import { merge } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { ConnectedDialogService } from '@morningstar/shared/ui-kit/connected-dialog';
import { StorageTypes } from '@morningstar/shared/base/storage-service';
import { StorageService } from '@morningstar/shared/base/storage-service';
import { ClientAutocompleteService } from '../../autocomplete/client/client-autocomplete.service';

@Component({
  selector: 'wsg-location',
  templateUrl: './default-location-dialog.component.html',
  styleUrls: ['./default-location-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DefaultLocationDialogComponent implements OnDestroy {

  // == STATE ===========================================================

  locationForm = this.formBuilder.group({
    location: [this.storedLocation],
    client: [this.storedClient]
  });

  enabled$ = this.storageService.enabled$;

  locationDisabled = false;

  // == SIDE EFFECTS ======================================================

  clientChange$ = this.clientAutocompleteService.clientChanged$.pipe(
    tap(() => {
      this.storageService.setItem(StorageTypes.client, this.client.value.tenantId);
      this.connectedDialogService.close();
    })
  );

  locationChange$ = this.location.valueChanges.pipe(
    tap(location => {
      if (location && this.storedLocation !== location) {
        this.storageService.setItem(StorageTypes.location, location);
      }
    })
  );

  toggleControls$ = this.enabled$.pipe(
    distinctUntilChanged(),
    tap(enabled => {
      if (!enabled) {
        this.locationForm.disable();
        this.locationDisabled = true;
      } else {
        this.locationForm.enable();
        this.locationDisabled = false;
      }
    })
  );

  // == SUBSCRIPTIONS ======================================================================

  subscription = merge(
    this.clientChange$,
    this.locationChange$,
    this.toggleControls$
  ).subscribe();

  // == LIFECYCLE ===========================================================================

  constructor(private formBuilder: FormBuilder,
              private connectedDialogService: ConnectedDialogService,
              private storageService: StorageService,
              private clientAutocompleteService: ClientAutocompleteService) {

  }

  ngOnDestroy(){
    this.subscription.unsubscribe();
  }

  // == HELPERS =====================================================================

  get location(): AbstractControl {
    return this.locationForm.get('location');
  }

  get client(): AbstractControl {
    return this.locationForm.get('client');
  }

  get storedLocation(): string {
    return this.storageService.getItem(StorageTypes.location);
  }

  get storedClient(): string {
    return this.storageService.getItem(StorageTypes.client);
  }
}
